// Guided answer steps for the five Level 1 Support Desk tickets.
//
// A ticket's `guided` array (src/domains/support/tickets.js) replaces the eight free-text
// fields with short, checkable steps. Stored per ticket as `record.guided = { stepId: value }`
// next to the existing `record.evidence` (commands run on the simulated endpoint).
//
// Value shapes:  choice → 'B'   multi → ['a','b']   order → ['x','y','z']
//                evidence → the exact output line the learner clicked
//                note → { parts: { partId: optionId }, text: '...' }
//
// Evidence is never trusted on its own: an evidence answer must match the step's pattern
// AND be a line of the stored output of the required command. Guest claims replay the
// commands first (guestClaim.js), so a forged output line cannot pass.

const MAX_TEXT = 4000;
const MAX_LINE = 300;
const norm = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();
const cmdKey = (s) => norm(s).toLowerCase();
const ids = (pairs) => (pairs || []).map(([id]) => id);

/** Outputs stored for one command (latest last). */
export function outputsFor(record, command) {
  return (record?.evidence || []).filter((e) => cmdKey(e?.command) === cmdKey(command)).map((e) => String(e?.output || ''));
}

/** The commands a step needs to have been run before it can be answered. */
export function stepCommands(step) {
  return step.kind === 'evidence' ? [step.pick.command] : (step.commands || []);
}

export function missingCommands(step, record) {
  return stepCommands(step).filter((c) => outputsFor(record, c).length === 0);
}

/** First output line of the required command that proves the step (admin / tests). */
export function findEvidenceLine(step, record) {
  if (step.kind !== 'evidence') return null;
  const re = new RegExp(step.pick.match, 'i');
  for (const out of outputsFor(record, step.pick.command).reverse()) {
    const line = out.split('\n').find((l) => re.test(l));
    if (line) return norm(line);
  }
  return null;
}

/** Shortest ticket note accepted (characters, spaces collapsed). */
export const MIN_NOTE = 10;
const NOTE_EMPTY = 'Write a short note first — a few words is enough.';

/** The full text of a saved note: builder lines (older saves) + the learner's own text. */
export function noteText(step, value) {
  if (!value || typeof value !== 'object') return '';
  return [builderText(step, value.parts || {}), String(value.text || '').trim()].filter(Boolean).join('\n');
}

/** The note without empty "Heading:" lines (an untouched template is not a note). */
export const noteContent = (text) => norm(String(text || '').split('\n').filter((l) => !/^\s*[^:\n]{1,40}:\s*$/.test(l)).join(' '));

/** Lines a strong note usually has (the headings, or the builder part labels). */
export const noteHeadings = (step) => step.headings || (step.parts || []).map((p) => p.label);

/** Non-blocking feedback: which of the usual lines the note already has ("Heading: something"). */
export function noteChecklist(step, text) {
  const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return noteHeadings(step).map((hd) => ({ label: hd, ok: new RegExp(`(^|\\n)[ \\t]*${esc(hd)}[ \\t]*:[ \\t]*\\S`, 'i').test(String(text || '')) }));
}

/** An example note for comparison (shown after the learner saves their own). */
export function exampleNote(step) {
  const answers = Object.fromEntries((step.parts || []).map((p) => [p.id, p.answer]));
  return [builderText(step, answers), step.exampleExtra || ''].filter(Boolean).join('\n');
}

/** Starter text for a free-text handoff: one "Heading: " line per expected heading. */
export function noteTemplate(step) {
  return (step.headings || (step.parts || []).map((p) => p.label)).map((h) => `${h}: `).join('\n');
}

/** Builder preview: "Issue: … · Checked: …" from the chosen parts. */
export function builderText(step, parts = {}) {
  return (step.parts || []).map((p) => {
    const opt = (p.options || []).find(([id]) => id === parts[p.id]);
    return opt ? `${p.label}: ${opt[1]}` : null;
  }).filter(Boolean).join('\n');
}

/** Bound and shape a value for a step; returns undefined when it does not fit the step. */
export function sanitizeGuidedValue(step, value) {
  if (!step) return undefined;
  switch (step.kind) {
    case 'choice': return ids(step.options).includes(value) ? value : undefined;
    case 'multi': {
      if (!Array.isArray(value)) return undefined;
      const ok = ids(step.options);
      return [...new Set(value.filter((v) => ok.includes(v)))];
    }
    case 'order': {
      const ok = ids(step.items);
      if (!Array.isArray(value) || value.length !== ok.length) return undefined;
      return [...value].sort().join('|') === [...ok].sort().join('|') ? [...value] : undefined;
    }
    case 'evidence': return typeof value === 'string' && value.trim() ? norm(value).slice(0, MAX_LINE) : undefined;
    case 'note': {
      if (!value || typeof value !== 'object') return undefined;
      const parts = {};
      for (const p of step.parts || []) if (ids(p.options).includes(value.parts?.[p.id])) parts[p.id] = value.parts[p.id];
      const text = typeof value.text === 'string' ? value.text.slice(0, MAX_TEXT) : '';
      return { parts, text };
    }
    default: return undefined;
  }
}

/**
 * Check one step. `record` supplies stored evidence for command-based steps.
 * @returns {{ ok: boolean, reason?: string }}
 */
export function checkGuidedStep(step, value, record = {}) {
  const missing = missingCommands(step, record);
  if (missing.length) return { ok: false, reason: `Run ${missing.join(' and ')} first.` };
  const v = sanitizeGuidedValue(step, value);
  if (v === undefined || (Array.isArray(v) && !v.length)) return { ok: false, reason: step.kind === 'note' ? NOTE_EMPTY : 'Choose an answer first.' };
  switch (step.kind) {
    case 'choice': return { ok: v === step.answer };
    case 'multi': return { ok: v.length === step.answer.length && step.answer.every((a) => v.includes(a)) };
    case 'order': return { ok: v.join('|') === step.answer.join('|') };
    case 'evidence': {
      if (!new RegExp(step.pick.match, 'i').test(v)) return { ok: false };
      const seen = outputsFor(record, step.pick.command).some((out) => out.split('\n').some((l) => norm(l) === v));
      return seen ? { ok: true } : { ok: false, reason: 'Pick a line from the command output.' };
    }
    case 'note': {
      // The ticket note is the learner's own record: any note of a few words is accepted.
      // (Feedback afterwards — noteChecklist + exampleNote — teaches what a strong note has, without blocking.)
      if (noteContent(noteText(step, v)).length < MIN_NOTE) return { ok: false, reason: NOTE_EMPTY };
      return { ok: true };
    }
    default: return { ok: false };
  }
}

/** Index of the first step that is not yet answered correctly (steps.length when all are). */
export function firstOpenStep(ticket, record = {}) {
  const steps = ticket.guided || [];
  const i = steps.findIndex((s) => !checkGuidedStep(s, record.guided?.[s.id], record).ok);
  return i < 0 ? steps.length : i;
}

/** Whole-ticket validation for guided tickets (same result shape as supportValidation). */
export function guidedValidation(ticket, record = {}) {
  const missing = [];
  for (const step of ticket.guided || []) {
    if (!checkGuidedStep(step, record.guided?.[step.id], record).ok) missing.push(`step “${step.title}”`);
  }
  if (!(record.evidence || []).length) missing.push('command evidence');
  return { ok: missing.length === 0, missing, unmet: [] };
}

/** Admin / tests: the full set of guided values for a reference answer, with evidence lines taken from `record`. */
export function referenceGuidedValues(ticket, answer, record = {}) {
  const values = {};
  for (const step of ticket.guided || []) {
    const v = step.kind === 'evidence' ? findEvidenceLine(step, record) : answer?.guided?.[step.id];
    if (v != null) values[step.id] = v;
  }
  return values;
}
